import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Car,
  FileWarning,
  Inbox,
  ListChecks,
  MessageCircle,
  Wallet,
} from 'lucide-react';
import type { AlertaDocumental } from '@erp/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, cn, Dialog, Skeleton } from '@erp/ui';
import { api } from '../lib/api';
import { type LeadItem } from '../lib/crm';
import { dataHora, moeda } from '../lib/formato';
import { useSessao } from '../lib/sessao';

interface Item {
  id: string;
  para: string;
  icone: ReactNode;
  texto: string;
  detalhe?: string;
  urgente?: boolean;
}

interface Fonte {
  nome: string;
  habilitada: boolean;
  consulta: { isPending: boolean; isError: boolean; fetchStatus: string };
  itens: Item[];
}

const POR_FONTE = 5;
/** Visíveis antes do "Ver mais": o bloco não empurra os indicadores para longe. */
const VISIVEIS = 6;

/**
 * Pendências: tudo o que pede ação agora, de todas as áreas, num lugar só.
 * Usa as mesmas rotas (e chaves de cache) das telas: cada papel só vê o que já veria lá,
 * e cada fonte falha sozinha. O mesmo cálculo alimenta o bloco do painel e o contador do topo.
 */
export function usePendencias() {
  const { usuario } = useSessao();
  const papel = usuario?.papel;
  const recursos = usuario?.recursos ?? [];
  const vende = papel === 'dono' || papel === 'gerente' || papel === 'vendedor';
  const crm = vende && recursos.includes('crm_basico');
  const central = vende && recursos.includes('central_atendimento');
  const caixa = papel === 'dono' || papel === 'financeiro';
  const fiscal = papel !== 'vendedor' && recursos.includes('nfe');
  const estoque = papel === 'dono' || papel === 'gerente' || papel === 'financeiro';

  const conversas = useQuery({
    queryKey: ['conversas', 'inicio'],
    queryFn: () =>
      api<{ id: string; naoLidas: number; cliente: { nome: string } | null }[]>(
        '/atendimento/conversas?status=aberta',
      ),
    enabled: central,
    refetchInterval: 30_000,
  });
  const leads = useQuery({
    queryKey: ['leads', 'inicio'],
    queryFn: () => api<LeadItem[]>('/leads'),
    enabled: crm,
  });
  const agenda = useQuery({
    queryKey: ['agenda'],
    queryFn: () =>
      api<
        {
          id: string;
          inicio: string;
          status: string;
          cliente: { nome: string } | null;
          veiculo: { descricao: string } | null;
        }[]
      >('/agenda'),
    enabled: crm,
  });
  const contas = useQuery({
    queryKey: ['financeiro', 'lancamentos', { situacao: 'vencido' }],
    queryFn: () =>
      api<{ itens: { id: string; tipo: 'pagar' | 'receber'; valorCentavos: number }[] }>(
        '/financeiro/lancamentos?situacao=vencido',
      ),
    enabled: caixa,
  });
  const alertas = useQuery({
    queryKey: ['fiscal', 'alertas'],
    queryFn: () => api<AlertaDocumental[]>('/fiscal/alertas'),
    enabled: fiscal,
  });

  const carros = useQuery({
    queryKey: ['pendencias', 'abertas'],
    queryFn: () =>
      api<
        {
          id: string;
          descricao: string;
          prazo: string | null;
          veiculoId: string;
          veiculo: { descricao: string; placa: string } | null;
        }[]
      >('/estoque/pendencias'),
    enabled: estoque,
  });

  const hoje = new Date().toDateString();
  const em3dias = new Date(Date.now() + 3 * 86_400_000).toLocaleDateString('sv-SE');
  const hojeIso = new Date().toLocaleDateString('sv-SE');
  const fontes: Fonte[] = [
    {
      nome: 'as conversas',
      habilitada: central,
      consulta: conversas,
      itens: (conversas.data ?? [])
        .filter((c) => c.naoLidas > 0)
        .map((c) => ({
          id: `cv-${c.id}`,
          para: `/atendimento/${c.id}`,
          icone: <MessageCircle />,
          texto: `${c.cliente?.nome ?? 'Cliente'} mandou mensagem`,
          detalhe: `${c.naoLidas} não ${c.naoLidas === 1 ? 'lida' : 'lidas'}`,
          urgente: true,
        })),
    },
    {
      nome: 'os leads',
      habilitada: crm,
      consulta: leads,
      itens: (leads.data ?? [])
        .filter((l) => l.semResposta)
        .map((l) => ({
          id: `ld-${l.id}`,
          para: l.clienteId ? `/clientes/${l.clienteId}` : '/leads',
          icone: <Inbox />,
          texto: `${l.nome} espera resposta`,
          urgente: true,
        })),
    },
    {
      nome: 'a agenda',
      habilitada: crm,
      consulta: agenda,
      itens: (agenda.data ?? [])
        .filter((a) => a.status === 'agendado' && new Date(a.inicio).toDateString() === hoje)
        .map((a) => ({
          id: `ag-${a.id}`,
          para: '/agenda',
          icone: <CalendarDays />,
          texto: `Test drive com ${a.cliente?.nome ?? 'cliente'}`,
          detalhe: [dataHora(a.inicio), a.veiculo?.descricao].filter(Boolean).join(' · '),
        })),
    },
    {
      nome: 'as contas',
      habilitada: caixa,
      consulta: contas,
      itens: (['receber', 'pagar'] as const).flatMap((tipo) => {
        const doTipo = (contas.data?.itens ?? []).filter((l) => l.tipo === tipo);
        if (!doTipo.length) return [];
        const n = doTipo.length;
        return [
          {
            id: `fin-${tipo}`,
            para: '/financeiro',
            icone: <Wallet />,
            texto: `${n} ${n === 1 ? 'conta' : 'contas'} a ${tipo} ${n === 1 ? 'vencida' : 'vencidas'}`,
            detalhe: moeda(doTipo.reduce((s, l) => s + l.valorCentavos, 0)),
            urgente: true,
          },
        ];
      }),
    },
    {
      nome: 'os alertas fiscais',
      habilitada: fiscal,
      consulta: alertas,
      itens: (alertas.data ?? [])
        .filter((a) => a.gravidade !== 'baixa')
        .map((a, i) => ({
          id: `fx-${i}`,
          para: a.link ?? '/fiscal',
          icone: <FileWarning />,
          texto: a.titulo,
          detalhe: a.detalhe,
          urgente: a.gravidade === 'alta',
        })),
    },
    {
      nome: 'as pendências dos carros',
      habilitada: estoque,
      consulta: carros,
      // Só o que vence em até 3 dias ou já venceu: o resto fica na aba Pendências do carro
      itens: (Array.isArray(carros.data) ? carros.data : [])
        .filter((p) => p.prazo !== null && p.prazo <= em3dias)
        .map((p) => ({
          id: `pv-${p.id}`,
          para: `/estoque/${p.veiculoId}?aba=pendencias`,
          icone: <Car />,
          texto: `${p.veiculo?.descricao ?? 'Carro'}: ${p.descricao}`,
          detalhe: `${p.prazo! < hojeIso ? 'Venceu' : 'Vence'} em ${p.prazo!.split('-').reverse().join('/')}`,
          urgente: p.prazo! < hojeIso,
        })),
    },
  ].filter((f) => f.habilitada);

  const carregando = fontes.some((f) => f.consulta.isPending && f.consulta.fetchStatus !== 'idle');
  const comErro = fontes.filter((f) => f.consulta.isError);
  const itens = fontes.flatMap((f) => f.itens.slice(0, POR_FONTE));
  const total = fontes.reduce((n, f) => n + f.itens.length, 0);
  const urgentes = fontes.reduce((n, f) => n + f.itens.filter((i) => i.urgente).length, 0);
  return { fontes, itens, total, urgentes, carregando, comErro };
}

export function Pendencias() {
  const [todas, setTodas] = useState(false);
  const { fontes, itens, carregando, comErro } = usePendencias();
  if (!fontes.length) return null;

  return (
    <Secao>
      {itens.length > 0 && <ListaPendencias itens={todas ? itens : itens.slice(0, VISIVEIS)} />}
      {itens.length > VISIVEIS && (
        <Button variant="ghost" size="sm" className="self-start" onClick={() => setTodas(!todas)}>
          {todas ? 'Ver menos' : `Ver mais ${itens.length - VISIVEIS}`}
        </Button>
      )}
      {carregando && <Skeleton className="h-10" />}
      {!carregando && !itens.length && !comErro.length && (
        <p className="text-sm text-muted-foreground">Tudo em dia. Nada pedindo ação agora.</p>
      )}
      {comErro.map((f) => (
        <p key={f.nome} className="text-sm text-destructive">
          Não foi possível carregar {f.nome}.
        </p>
      ))}
    </Secao>
  );
}

function ListaPendencias({ itens, aoAbrir }: { itens: Item[]; aoAbrir?: () => void }) {
  return (
    <ul className="divide-y">
      {itens.map((i) => (
        <li key={i.id}>
          <Link
            to={i.para}
            onClick={aoAbrir}
            className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent [&_svg]:size-4 [&_svg]:shrink-0"
          >
            <span className={i.urgente ? 'text-destructive' : 'text-muted-foreground'}>
              {i.icone}
            </span>
            {/* Uma linha só por pendência: texto e detalhe lado a lado */}
            <span className="min-w-0 flex-1 truncate font-medium">{i.texto}</span>
            {i.detalhe && (
              <span className="max-w-[40%] shrink-0 truncate text-xs text-muted-foreground">
                {i.detalhe}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Contador do topo: quantas pendências pedem ação agora, visível em qualquer tela.
 * Vermelho quando há algo urgente (cliente esperando, conta vencida, prazo estourado).
 */
export function ContadorPendencias() {
  const [aberto, setAberto] = useState(false);
  const { fontes, itens, total, urgentes, carregando, comErro } = usePendencias();
  if (!fontes.length) return null;
  const rotulo = total
    ? `Pendências (${total}${urgentes ? `, ${urgentes} ${urgentes === 1 ? 'urgente' : 'urgentes'}` : ''})`
    : 'Pendências (nenhuma)';
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={rotulo}
        title={rotulo}
        onClick={() => setAberto(true)}
      >
        <ListChecks />
        {total > 0 && (
          <span
            aria-hidden
            className={cn(
              'absolute right-1 top-1 min-w-4 rounded-full px-1 text-[10px] leading-4',
              urgentes ? 'bg-destructive text-white' : 'bg-warning text-foreground',
            )}
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </Button>
      <Dialog
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Pendências"
        descricao="O que pede ação agora, de todas as áreas."
      >
        <div className="flex max-h-[70dvh] flex-col gap-2 overflow-y-auto">
          {itens.length > 0 && <ListaPendencias itens={itens} aoAbrir={() => setAberto(false)} />}
          {carregando && <Skeleton className="h-10" />}
          {!carregando && !itens.length && !comErro.length && (
            <p className="text-sm text-muted-foreground">Tudo em dia. Nada pedindo ação agora.</p>
          )}
          {comErro.map((f) => (
            <p key={f.nome} className="text-sm text-destructive">
              Não foi possível carregar {f.nome}.
            </p>
          ))}
        </div>
      </Dialog>
    </>
  );
}

function Secao({ children }: { children: ReactNode }) {
  const id = useId();
  return (
    <Card>
      <section aria-labelledby={id}>
        <CardHeader>
          <CardTitle id={id} className="text-base">
            Pendências
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">{children}</CardContent>
      </section>
    </Card>
  );
}
